import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { trackPricingCta, trackQuestionCta } from "@/lib/analytics";

const navLinks = [
  { label: "Dashboard", href: "#dashboard" },
  { label: "Action plan", href: "#action-plan" },
  { label: "Pricing", href: "#pricing" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, link) => {
    if (link.isRoute) return;
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + link.href);
      return;
    }
    const el = document.querySelector(link.href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#030305]/85 backdrop-blur-md border-b border-white/[0.06]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 flex items-center justify-between h-16 lg:h-20">
        <Link
          to="/"
          className="flex items-center gap-2 text-white"
          data-testid="header-logo"
        >
          <div className="w-7 h-7 rounded-md bg-[#D4FF4A] flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="#030305" strokeWidth="1.6" />
              <circle cx="12" cy="12" r="4.5" stroke="#030305" strokeWidth="1.6" />
              <circle cx="12" cy="12" r="1.5" fill="#030305" />
              <line x1="12" y1="0.5" x2="12" y2="3.5" stroke="#030305" strokeWidth="1.6" />
              <line x1="12" y1="20.5" x2="12" y2="23.5" stroke="#030305" strokeWidth="1.6" />
              <line x1="0.5" y1="12" x2="3.5" y2="12" stroke="#030305" strokeWidth="1.6" />
              <line x1="20.5" y1="12" x2="23.5" y2="12" stroke="#030305" strokeWidth="1.6" />
            </svg>
          </div>
          <span className="font-display font-semibold text-[17px] tracking-tight">
            ICPIntel
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.isRoute ? (
              <Link
                key={link.label}
                to={link.href}
                className="text-[14px] text-[#A1A1AA] hover:text-white transition-colors"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNav(e, link)}
                className="text-[14px] text-[#A1A1AA] hover:text-white transition-colors"
              >
                {link.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://tidycal.com/nick-christensen/30-minute-meeting"
            onClick={() => trackQuestionCta("header_desktop")}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[14px] text-[#A1A1AA] hover:text-white transition-colors"
          >
            Ask a question
          </a>
          <a
            href="#pricing"
            onClick={() => trackPricingCta("header_desktop")}
            className="px-4 py-2 bg-[#D4FF4A] text-[#030305] text-[14px] font-medium rounded-full hover:bg-white transition-colors"
            data-testid="header-cta"
          >
            Get the $999 beta
          </a>
        </div>

        <button
          className="lg:hidden text-white"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-white/[0.06] bg-[#030305]/95 backdrop-blur-md">
          <div className="px-6 py-6 space-y-5">
            {navLinks.map((link) =>
              link.isRoute ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="block text-[15px] text-[#A1A1AA] hover:text-white"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNav(e, link)}
                  className="block text-[15px] text-[#A1A1AA] hover:text-white"
                >
                  {link.label}
                </a>
              )
            )}
            <div className="pt-4 border-t border-white/[0.06] flex flex-col gap-3">
              <a
                href="https://tidycal.com/nick-christensen/30-minute-meeting"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[15px] text-[#A1A1AA]"
                onClick={() => {
                  trackQuestionCta("header_mobile");
                  setMobileOpen(false);
                }}
              >
                Ask a question
              </a>
              <a
                href="#pricing"
                className="inline-flex justify-center px-5 py-3 bg-[#D4FF4A] text-[#030305] text-[14px] font-semibold rounded-full"
                onClick={() => {
                  trackPricingCta("header_mobile");
                  setMobileOpen(false);
                }}
              >
                Get the $999 beta
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
